import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Drive, DriveItem, Site } from '@microsoft/microsoft-graph-types';
import { listChildren, listDrives, listSites } from '../../lib/graph/sharepoint';
import {
  connectMicrosoftAccount,
  NO_MS_ACCOUNT_ERROR_CODE,
} from '../../lib/graph/msalClient';
import { driveItemToInput, isFile } from '@repancy/graph-core';
import { HelpTip } from '../ui/HelpTip';

const FONT = "var(--xp-font-body)";

type WizardStep = 'site' | 'drive' | 'folder' | 'review' | 'progress' | 'done';
type LinkMode = 'link' | 'copy';

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
};

const dialogStyle: React.CSSProperties = {
  width: 620,
  height: 480,
  background: '#ece9d8',
  borderStyle: 'solid',
  borderWidth: 2,
  boxShadow: '3px 3px 0 #000',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: FONT,
  fontSize: 12,
};

const titleBarStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, var(--xp-selection), #1a4ab8)',
  color: 'white',
  padding: '3px 6px',
  fontWeight: 'bold',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  padding: 14,
  overflow: 'auto',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 6,
  padding: '8px 12px',
  borderTop: '1px solid #808080',
};

function Btn({ label, onClick, disabled, primary }: { label: string; onClick: () => void; disabled?: boolean; primary?: boolean }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        minWidth: 90,
        height: 26,
        padding: '0 12px',
        background: '#ece9d8',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        fontWeight: primary ? 'bold' : 'normal',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? '#808080' : '#000',
      }}
    >
      {label}
    </button>
  );
}

/** The wizard collects a flat selection of driveItems. The host (Convex layer)
 * decides what `matterId` they land in and runs `upsertFromExternal` per item. */
export interface ImportSelection {
  siteId: string;
  driveId: string;
  items: DriveItem[];
  linkMode: LinkMode;
}

export interface ImportWizardProps {
  open: boolean;
  onCancel: () => void;
  /**
   * Called when the user clicks "Import". Host is responsible for the actual
   * upsertFromExternal mutations + audit log entries. The wizard then advances
   * to a progress view driven by `progress` (0..items.length) and `error`.
   */
  onImport: (selection: ImportSelection) => Promise<void>;
  /** Set by the host while onImport is running. */
  progress?: { completed: number; total: number; errors: number };
}

/**
 * Multi-step SharePoint import wizard. Each step uses the real Graph API via
 * lib/graph/sharepoint.ts; tests can swap the module via vi.mock.
 */
export function ImportWizard({ open, onCancel, onImport, progress }: ImportWizardProps) {
  const [step, setStep] = useState<WizardStep>('site');
  const [sites, setSites] = useState<Site[]>([]);
  const [drives, setDrives] = useState<Drive[]>([]);
  const [children, setChildren] = useState<DriveItem[]>([]);
  const [siteQuery, setSiteQuery] = useState('');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<Drive | null>(null);
  const [folderStack, setFolderStack] = useState<DriveItem[]>([]); // breadcrumb
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [linkMode, setLinkMode] = useState<LinkMode>('link');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsMsConnect, setNeedsMsConnect] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const handleError = useCallback((err: unknown) => {
    const code = (err as { code?: unknown } | null)?.code;
    if (code === NO_MS_ACCOUNT_ERROR_CODE) {
      setNeedsMsConnect(true);
      setError(null);
      return;
    }
    setError(err instanceof Error ? err.message : String(err));
  }, []);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      await connectMicrosoftAccount();
      setNeedsMsConnect(false);
      // Bump the step state to retrigger whichever fetch effect was
      // gated on the missing account. Re-setting the same step value
      // is a no-op for React, so toggle through a sibling.
      setStep((s) => s);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setConnecting(false);
    }
  }, []);

  // Reset everything when reopened.
  useEffect(() => {
    if (open) {
      setStep('site');
      setSites([]);
      setDrives([]);
      setChildren([]);
      setSelectedSite(null);
      setSelectedDrive(null);
      setFolderStack([]);
      setSelectedIds(new Set());
      setLinkMode('link');
      setError(null);
      setNeedsMsConnect(false);
    }
  }, [open]);

  // Fetch sites on entering 'site' step (and when query changes).
  useEffect(() => {
    if (!open || step !== 'site') return;
    setLoading(true);
    setError(null);
    listSites(siteQuery || undefined)
      .then(setSites)
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [open, step, siteQuery]);

  // Fetch drives when a site is picked.
  useEffect(() => {
    if (step !== 'drive' || !selectedSite?.id) return;
    setLoading(true);
    setError(null);
    listDrives(selectedSite.id)
      .then(setDrives)
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [step, selectedSite]);

  // Fetch children when a drive is picked or folder changes.
  useEffect(() => {
    if (step !== 'folder' || !selectedDrive?.id) return;
    setLoading(true);
    setError(null);
    const folderId = folderStack[folderStack.length - 1]?.id ?? 'root';
    listChildren(selectedDrive.id, folderId)
      .then(setChildren)
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [step, selectedDrive, folderStack]);

  const selectedFiles = useMemo(
    () => children.filter((c) => c.id && selectedIds.has(c.id) && isFile(c)),
    [children, selectedIds],
  );

  if (!open) return null;

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleEnterFolder(folder: DriveItem) {
    setFolderStack((prev) => [...prev, folder]);
    setSelectedIds(new Set()); // selection is per-folder for simplicity
  }

  function handleBreadcrumbUp(depth: number) {
    setFolderStack((prev) => prev.slice(0, depth));
    setSelectedIds(new Set());
  }

  async function runImport() {
    if (!selectedSite?.id || !selectedDrive?.id) return;
    setStep('progress');
    try {
      await onImport({
        siteId: selectedSite.id,
        driveId: selectedDrive.id,
        items: selectedFiles,
        linkMode,
      });
      setStep('done');
    } catch (err) {
      handleError(err);
    }
  }

  const stepTitles: Record<WizardStep, string> = {
    site: 'Step 1 of 4: Pick a SharePoint site',
    drive: 'Step 2 of 4: Pick a document library',
    folder: 'Step 3 of 4: Pick documents',
    review: 'Step 4 of 4: Confirm import',
    progress: 'Importing…',
    done: 'Done',
  };

  return (
    <div style={overlayStyle} data-testid="import-wizard-overlay" onClick={onCancel}>
      <div style={dialogStyle} data-testid="import-wizard" onClick={(e) => e.stopPropagation()}>
        <div style={titleBarStyle}>Import from SharePoint — {stepTitles[step]}</div>
        <div style={bodyStyle}>
          {needsMsConnect && (
            <div
              data-testid="import-wizard-needs-ms-connect"
              style={{
                marginBottom: 10,
                padding: 10,
                background: '#fffbcc',
                border: '1px solid #b08800',
                color: '#000',
                lineHeight: 1.4,
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                Connect a Microsoft account to import from SharePoint
              </div>
              <div style={{ marginBottom: 8 }}>
                Repancy needs delegated access to your Microsoft account so
                it can list the SharePoint sites, libraries, and files you
                have permission to import. A sign-in popup will open.
              </div>
              <HelpTip k="importWizard.connectMicrosoft" wrap>
                <span>
                  <Btn
                    label={connecting ? 'Opening sign-in…' : 'Connect Microsoft account'}
                    onClick={handleConnect}
                    disabled={connecting}
                    primary
                  />
                </span>
              </HelpTip>
            </div>
          )}
          {error && <div style={{ color: '#800', marginBottom: 8 }}>{error}</div>}
          {loading && <div style={{ color: '#444' }}>Loading…</div>}

          {step === 'site' && (
            <>
              <input
                type="text"
                placeholder="Search sites…"
                value={siteQuery}
                onChange={(e) => setSiteQuery(e.target.value)}
                style={{ width: '100%', padding: 4, marginBottom: 8, fontFamily: FONT }}
              />
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {sites.map((s) => (
                  <li
                    key={s.id}
                    onClick={() => { setSelectedSite(s); setStep('drive'); }}
                    style={{
                      padding: '6px 8px',
                      cursor: 'pointer',
                      background: selectedSite?.id === s.id ? 'var(--xp-selection)' : 'transparent',
                      color: selectedSite?.id === s.id ? 'white' : '#000',
                    }}
                  >
                    📁 {s.displayName ?? s.name}
                    {s.description && <span style={{ color: '#666' }}> — {s.description}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}

          {step === 'drive' && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {drives.map((d) => (
                <li
                  key={d.id}
                  onClick={() => { setSelectedDrive(d); setStep('folder'); }}
                  style={{ padding: '6px 8px', cursor: 'pointer' }}
                >
                  📚 {d.name} <span style={{ color: '#666' }}>({d.driveType})</span>
                </li>
              ))}
            </ul>
          )}

          {step === 'folder' && (
            <>
              <div style={{ marginBottom: 8, fontSize: 11 }}>
                <span
                  onClick={() => handleBreadcrumbUp(0)}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {selectedDrive?.name ?? 'Drive'}
                </span>
                {folderStack.map((f, i) => (
                  <span key={f.id}>
                    {' / '}
                    <span
                      onClick={() => handleBreadcrumbUp(i + 1)}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {f.name}
                    </span>
                  </span>
                ))}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {children.map((c) => {
                  const checked = c.id ? selectedIds.has(c.id) : false;
                  const file = isFile(c);
                  return (
                    <li
                      key={c.id}
                      style={{
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {file ? (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => c.id && toggleSelect(c.id)}
                        />
                      ) : (
                        <span style={{ width: 16 }} />
                      )}
                      <span
                        onClick={() => !file && handleEnterFolder(c)}
                        style={{ cursor: file ? 'default' : 'pointer' }}
                      >
                        {file ? '📄' : '📂'} {c.name}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div style={{ marginTop: 12, fontSize: 11, color: '#444' }}>
                {selectedIds.size} selected
              </div>
            </>
          )}

          {step === 'review' && (
            <>
              <p>Importing <strong>{selectedFiles.length}</strong> file(s) from <strong>{selectedSite?.displayName ?? selectedSite?.name}</strong> / <strong>{selectedDrive?.name}</strong>.</p>
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', marginBottom: 6 }}>
                  <input
                    type="radio"
                    name="link-mode"
                    checked={linkMode === 'link'}
                    onChange={() => setLinkMode('link')}
                  /> Link to original (future edits sync; recommended)
                </label>
                <label style={{ display: 'block' }}>
                  <input
                    type="radio"
                    name="link-mode"
                    checked={linkMode === 'copy'}
                    onChange={() => setLinkMode('copy')}
                  /> Copy in (one-shot; no sync, no save-back)
                </label>
              </div>
              <h4 style={{ marginTop: 12 }}>Preview</h4>
              <ul style={{ fontSize: 11, paddingLeft: 18 }}>
                {selectedFiles.slice(0, 10).map((f) => {
                  try {
                    const input = driveItemToInput(f, selectedSite?.id ?? '');
                    return <li key={f.id}>{input.title} — {input.documentType}</li>;
                  } catch {
                    return <li key={f.id} style={{ color: '#800' }}>{f.name} (skipped — missing fields)</li>;
                  }
                })}
                {selectedFiles.length > 10 && <li>… and {selectedFiles.length - 10} more</li>}
              </ul>
            </>
          )}

          {step === 'progress' && progress && (
            <>
              <p>Importing {progress.completed} of {progress.total}…</p>
              <div style={{
                width: '100%',
                height: 20,
                background: 'white',
                border: '1px solid #808080',
                position: 'relative',
              }}>
                <div style={{
                  width: `${(progress.completed / Math.max(1, progress.total)) * 100}%`,
                  height: '100%',
                  background: 'var(--xp-selection)',
                }} />
              </div>
              {progress.errors > 0 && (
                <p style={{ color: '#800', marginTop: 6 }}>{progress.errors} item(s) failed.</p>
              )}
            </>
          )}

          {step === 'done' && (
            <p>Import complete.</p>
          )}
        </div>

        <div style={footerStyle}>
          <HelpTip k="importWizard.cancel" wrap>
            <span>
              <Btn label="Cancel" onClick={onCancel} disabled={step === 'progress'} />
            </span>
          </HelpTip>
          <div style={{ display: 'flex', gap: 6 }}>
            {step === 'drive' && (
              <HelpTip k="importWizard.back" wrap>
                <span><Btn label="< Back" onClick={() => setStep('site')} /></span>
              </HelpTip>
            )}
            {step === 'folder' && (
              <>
                <HelpTip k="importWizard.back" wrap>
                  <span><Btn label="< Back" onClick={() => setStep('drive')} /></span>
                </HelpTip>
                <HelpTip k="importWizard.next" wrap>
                  <span><Btn label="Next >" onClick={() => setStep('review')} disabled={selectedIds.size === 0} primary /></span>
                </HelpTip>
              </>
            )}
            {step === 'review' && (
              <>
                <HelpTip k="importWizard.back" wrap>
                  <span><Btn label="< Back" onClick={() => setStep('folder')} /></span>
                </HelpTip>
                <Btn label="Import" onClick={runImport} primary />
              </>
            )}
            {step === 'done' && <Btn label="Close" onClick={onCancel} primary />}
          </div>
        </div>
      </div>
    </div>
  );
}
